import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy, 
  where,
  Timestamp 
} from 'firebase/firestore';
import { db } from './firebase';

export interface Brand {
  id: string;
  name: string;
  description?: string;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Get all brands
 */
export const getBrands = async (): Promise<Brand[]> => {
  try {
    console.log('Fetching brands from Firestore...');
    const brandsCollection = collection(db, 'brands');
    const snapshot = await getDocs(query(brandsCollection, orderBy('name')));
    
    const brands = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
    })) as Brand[];
    
    console.log('Brands fetched:', brands.length);
    return brands;
  } catch (error) {
    console.error('Error fetching brands:', error);
    throw new Error('Failed to fetch brands');
  }
};

/**
 * Fetch all brands (alias for getBrands for consistency)
 */
export const fetchAllBrands = async (): Promise<Brand[]> => {
  return getBrands();
};

/**
 * Create a new brand
 */
export const createBrand = async (name: string, description?: string): Promise<Brand> => {
  try {
    // Check if brand already exists (case-insensitive)
    const brandsCollection = collection(db, 'brands');
    const q = query(brandsCollection, where('name', '==', name.trim()));
    const existingSnapshot = await getDocs(q);
    
    if (!existingSnapshot.empty) {
      throw new Error('Brand already exists');
    }

    const now = Timestamp.now();
    const brandData = {
      name: name.trim(),
      description: description?.trim() || '',
      createdAt: now,
      updatedAt: now,
    };

    const docRef = await addDoc(brandsCollection, brandData);
    
    return {
      id: docRef.id,
      name: brandData.name,
      description: brandData.description,
      createdAt: now.toDate(),
      updatedAt: now.toDate(),
    };
  } catch (error) {
    console.error('Error creating brand:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to create brand');
  }
};

/**
 * Update an existing brand
 */
export const updateBrand = async (id: string, name: string, description?: string): Promise<void> => {
  try {
    const brandDoc = doc(db, 'brands', id);
    const updateData = {
      name: name.trim(),
      description: description?.trim() || '',
      updatedAt: Timestamp.now(),
    };

    await updateDoc(brandDoc, updateData);
  } catch (error) {
    console.error('Error updating brand:', error);
    throw new Error('Failed to update brand');
  }
};

/**
 * Delete a brand
 */
export const deleteBrand = async (id: string): Promise<void> => {
  try {
    const brandDoc = doc(db, 'brands', id);
    await deleteDoc(brandDoc);
  } catch (error) {
    console.error('Error deleting brand:', error);
    throw new Error('Failed to delete brand');
  }
};

/**
 * Search brands by name
 */
export const searchBrands = async (searchTerm: string): Promise<Brand[]> => {
  try {
    const brands = await getBrands();
    const lowercaseSearch = searchTerm.toLowerCase();
    
    return brands.filter(brand => 
      brand.name.toLowerCase().includes(lowercaseSearch)
    );
  } catch (error) {
    console.error('Error searching brands:', error);
    throw new Error('Failed to search brands');
  }
}; 